import React from 'react';
import { Avatar, Badge, Button, Dropdown, Layout, List, Popover, Space, Tag, Typography } from 'antd';
import { BellOutlined, LogoutOutlined, MenuUnfoldOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { AdminNotification } from '../../services/notification.service';

const { Header } = Layout;
const { Title, Text } = Typography;

interface AdminHeaderProps {
  isMobile: boolean;
  title: string;
  onToggleSidebar?: () => void;
  onUserAction: (key: string) => void;
  notifications: AdminNotification[];
  notificationsLoading: boolean;
  notificationsError: string | null;
  onNotificationsRetry: () => void;
}

const userDropdownItems: MenuProps['items'] = [
  {
    key: 'profile',
    icon: <UserOutlined />,
    label: 'Hồ sơ',
  },
  {
    key: 'settings',
    icon: <SettingOutlined />,
    label: 'Cài đặt',
  },
  { type: 'divider' },
  {
    key: 'logout',
    icon: <LogoutOutlined />,
    label: 'Đăng xuất',
    danger: true,
  },
];

const typeLabelMap: Record<string, string> = {
  success: 'thành công',
  warning: 'cảnh báo',
  info: 'thông tin',
};

const typeColorMap: Record<string, string> = {
  success: 'green',
  warning: 'gold',
  info: 'blue',
};

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  isMobile,
  title,
  onToggleSidebar,
  onUserAction,
  notifications,
  notificationsLoading,
  notificationsError,
  onNotificationsRetry,
}) => {
  const notificationContent = notificationsError ? (
    <Space direction="vertical" size={8} style={{ width: 280 }}>
      <Typography.Text type="danger">{notificationsError}</Typography.Text>
      <Button size="small" onClick={onNotificationsRetry}>Thử lại</Button>
    </Space>
  ) : notificationsLoading ? (
    <Typography.Text type="secondary">Đang tải thông báo...</Typography.Text>
  ) : notifications.length === 0 ? (
    <Typography.Text type="secondary">Không có thông báo</Typography.Text>
  ) : (
    <div style={{ width: 320 }}>
      <List
        dataSource={notifications}
        renderItem={(item) => (
          <List.Item style={{ padding: '8px 0' }}>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography.Text style={{ fontSize: 13 }}>{item.title}</Typography.Text>
                <Tag color={typeColorMap[item.type] || 'blue'} style={{ marginInlineEnd: 0 }}>
                  {typeLabelMap[item.type] || item.type}
                </Tag>
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {item.time}
              </Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Header
      style={{
        background: '#fff',
        padding: isMobile ? '0 12px' : '0 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        zIndex: 1,
        height: 56,
        lineHeight: '56px',
      }}
    >
      <Space size={12}>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuUnfoldOutlined style={{ fontSize: 18 }} />}
            onClick={onToggleSidebar}
            aria-label="Mở thanh bên"
          />
        )}
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
      </Space>

      <Space size={isMobile ? 8 : 16}>
        <Popover
          trigger="click"
          placement="bottomRight"
          content={notificationContent}
        >
          <Badge count={notifications.length} size="small">
            <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
          </Badge>
        </Popover>
        <Dropdown
          menu={{
            items: userDropdownItems,
            onClick: ({ key }) => onUserAction(String(key)),
          }}
          placement="bottomRight"
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar icon={<UserOutlined />} />
            <Text>Quản trị viên</Text>
          </Space>
        </Dropdown>
      </Space>
    </Header>
  );
};

export default AdminHeader;
